import geometry from '../../../scene/util/geometry';
import tacHelpers from '../tacHelpers';
import sorter from './sorter';
import removeItem from './removeItem';
import _ from 'lodash';
import { replace } from '../replace';
import makeSpaceForChild from './makeSpaceForChild';
import connectParts from './connectParts';
import constants from '../../../settings/constants';
import productService from '../../../services/products';
import { isFixedRoom } from '../../../util/room';

function closer(a, b) {
  return a.x > b.x ? 1 : a.x < b.x ? -1 : 0;
}

function getMovedX(oldItem, newItem) {
  return oldItem.x - newItem.x && oldItem.x - newItem.x;
}

function closest(items, direction = 'right') {
  if (direction === 'right') {
    return items.sort(closer)[0];
  }
  return items.sort(closer).reverse()[0];
}

function moveRight(item, originalItem, newItems, tac, initial = false) {
  const fullSizeItem = tacHelpers.getFullSize(item);
  const originalFullSizeItem = tacHelpers.getFullSize(originalItem);
  const skippedItems = !initial
    ? newItems.filter(item => item.x <= originalItem.x)
    : [];
  const items = newItems.filter(right => right.x > originalItem.x);

  const collidingItems = geometry.getCollidingRects(
    fullSizeItem,
    items.map(item => Object.assign({}, item, tacHelpers.getFullSize(item)))
  );

  if (collidingItems.length === 0) {
    return [...items, ...skippedItems];
  }

  const closestItem = closest(collidingItems, 'right');

  let distance;
  if (
    originalFullSizeItem.verticalExtensionPoint &&
    originalFullSizeItem.verticalExtensionPoint >
      closestItem.y + closestItem.height
  ) {
    /*
    Current case being a smaller frame/shelving unit placed under a clothes rail.
    */
    if (item.x + item.width - closestItem.x > 0) {
      distance = item.x + item.width - closestItem.x;
    } else {
      // No need to move.
      distance = 0;
    }
  } else {
    const overlappingItem =
      tacHelpers.hasExtClothesRail(fullSizeItem) ||
      !geometry.collides(originalItem, closestItem)
        ? fullSizeItem
        : tacHelpers.withAllowedOverlaps(fullSizeItem, tac, closestItem);
    distance = overlappingItem.x + overlappingItem.width - closestItem.x;
  }

  const nonFullSizeClosestItem = items.find(
    item => item.itemid === closestItem.itemid
  );
  const movedItem = {
    ...nonFullSizeClosestItem,
    x: closestItem.x + distance,
  };

  const otherItems = items.filter(i => i.itemid !== movedItem.itemid);

  const remaining = moveRight(
    movedItem,
    nonFullSizeClosestItem,
    otherItems,
    tac
  );
  const reaminingCollisions = geometry.getCollidingRects(
    fullSizeItem,
    remaining.map(item => Object.assign({}, item, tacHelpers.getFullSize(item)))
  );

  if (reaminingCollisions.length === 0) {
    return [movedItem, ...skippedItems].concat(remaining);
  }

  const retry = moveRight(item, originalItem, remaining, tac);

  return [movedItem, ...skippedItems].concat(retry);
}

function moveLeft(item, originalItem, newItems, tac, initial = false) {
  const fullSizeItem = tacHelpers.getFullSize(item);
  const skippedItems = !initial
    ? newItems.filter(item => item.x >= originalItem.x)
    : [];
  const items = newItems.filter(left => left.x < originalItem.x);

  const collidingItems = geometry.getCollidingRects(
    fullSizeItem,
    items.map(item => Object.assign({}, item, tacHelpers.getFullSize(item)))
  );
  if (collidingItems.length === 0) {
    return [...items, ...skippedItems];
  }
  const closestItem = closest(collidingItems, 'left');

  const nonFullSizeClosestItem = items.find(
    item => item.itemid === closestItem.itemid
  );

  let distance;
  if (
    closestItem.verticalExtensionPoint &&
    closestItem.verticalExtensionPoint > fullSizeItem.y + fullSizeItem.height
  ) {
    /*
    Current case being a smaller frame/shelving unit placed under a clothes rail.
    */
    if (nonFullSizeClosestItem.x + nonFullSizeClosestItem.width > item.x) {
      distance =
        item.x - (nonFullSizeClosestItem.x + nonFullSizeClosestItem.width);
    } else {
      // No need to move.
      distance = 0;
    }
  } else {
    const overlappingItem =
      tacHelpers.hasExtClothesRail(closestItem) ||
      !geometry.collides(originalItem, closestItem)
        ? fullSizeItem
        : tacHelpers.withAllowedOverlaps(fullSizeItem, tac, closestItem);
    distance = overlappingItem.x - closestItem.x - closestItem.width;
  }

  const movedItem = {
    ...nonFullSizeClosestItem,
    x: closestItem.x + distance,
  };

  const otherItems = items.filter(i => i.itemid !== movedItem.itemid);

  const remaining = moveLeft(
    movedItem,
    nonFullSizeClosestItem,
    otherItems,
    tac
  );

  const reaminingCollisions = geometry.getCollidingRects(
    fullSizeItem,
    remaining.map(item => Object.assign({}, item, tacHelpers.getFullSize(item)))
  );

  if (reaminingCollisions.length === 0) {
    return [movedItem, ...skippedItems].concat(remaining);
  }

  const retry = moveLeft(item, originalItem, remaining, tac);

  return [movedItem, ...skippedItems].concat(retry);
}

function tryMoveItems(
  item,
  oldItem,
  moveNewItemX,
  otherItems,
  potentialBlockers,
  tac
) {
  const recenteredItem = { ...item, x: item.x + moveNewItemX };
  const groupedItems = _.partition(otherItems, productService.isDependentItem);
  otherItems = groupedItems[1];

  const rightItems = moveRight(recenteredItem, oldItem, otherItems, tac, true);
  const leftItems = moveLeft(recenteredItem, oldItem, otherItems, tac, true);
  const centerItems = otherItems.filter(center => center.x === item.x);
  const allItems = [
    recenteredItem,
    ...leftItems,
    ...rightItems,
    ...centerItems,
    ...groupedItems[0],
  ];

  const uniqueItems = _.uniqBy(allItems, 'itemid');
  if (uniqueItems.length !== allItems.length) {
    throw new Error('Duplicate items after move');
  }

  let newSize;
  let newLimits;
  let newMaxX;
  const isFixedContext = isFixedRoom();

  if (isFixedContext) {
    newLimits = tacHelpers.getLimits({
      items: allItems,
    });
    newMaxX = newLimits.max.x;
  } else {
    newSize = tacHelpers.getSize({
      items: allItems,
    });
    newMaxX = newSize.width;
  }

  // Assume move validity until proven guilty
  let result = { valid: true, recenteredItem, allItems };
  const wallLimits = tacHelpers.getWallLimits(tac);

  if (newMaxX > constants.ROOM_MAX.width) {
    return {
      ...result,
      valid: false,
      invalidityMargin: newMaxX - constants.ROOM_MAX.width,
    };
  } else if (isFixedContext && newLimits.max.x > wallLimits.max.x) {
    return {
      ...result,
      valid: false,
      invalidityMargin: newLimits.max.x - wallLimits.max.x,
    };
  } else if (isFixedContext && newLimits.min.x < wallLimits.min.x) {
    return {
      ...result,
      valid: false,
      invalidityMargin: -newLimits.min.x,
    };
  } else if (!potentialBlockers || !potentialBlockers.length) {
    return result;
  } else {
    // ...and we also won't allow neither one of the blockers two top ancestors to move at all
    const allItemsGlobalPos = tacHelpers.getAllItems(allItems).map(item => {
      return {
        ...item,
        ...tacHelpers.getGlobalCoords(item, { items: allItems }),
      };
    });

    potentialBlockers.forEach(blocker => {
      const topAncestorGlobal = allItemsGlobalPos.find(
        item => item.itemid === blocker.topAncestor.itemid
      );
      const connectedGlobal = allItemsGlobalPos.find(
        item => item.itemid === blocker.connectsTo.itemid
      );
      const invalidityMargin =
        (topAncestorGlobal &&
          getMovedX(blocker.topAncestor, topAncestorGlobal)) ||
        (connectedGlobal && getMovedX(blocker.connectsTo, connectedGlobal));

      if (invalidityMargin) {
        result = {
          ...result,
          valid: false,
          invalidityMargin,
        };
      }
    });
  }

  return result;
}

/**
 * Gets a new version of the TAC with a specific item updated
 * @param {*} model
 * @param {*} item
 * @param {*} parent
 * @param {*} options
 * @returns {Object}
 */
export default function updateItem(model, item, parent = model, options = {}) {
  if (!item.itemid) {
    throw new Error('Cannot update an item that is not already in the model');
  }

  if (parent !== model) {
    parent = tacHelpers.getItem(model, parent.itemid);
  }

  let previousParent;
  const oldItem = tacHelpers.getItem(model, item.itemid);
  const moveNewItemX =
    options.moveOthers && parent === model
      ? Math.ceil((oldItem.width - item.width) / 2)
      : 0;

  if (
    !parent.items ||
    !parent.items.find(child => child.itemid === item.itemid)
  ) {
    previousParent = tacHelpers.getParent(model, item);

    if (previousParent) {
      const newModel = removeItem(model, item, previousParent);

      if (model === parent) {
        parent = newModel;
      } else {
        // update parent if item was moved here from a descendant
        parent = tacHelpers.getItem(newModel, parent.itemid);
      }

      model = newModel;
    }
  }

  const otherItems = parent.items
    ? parent.items.filter(i => i.itemid !== item.itemid)
    : [];

  let updatedItem;
  let allItems;
  if (!options.moveOthers) {
    updatedItem = { ...item, x: item.x + moveNewItemX };
    allItems = [updatedItem, ...otherItems];
  } else {
    const potentialBlockers = tacHelpers.getPotentialBlockers(
      { items: parent.items || [] },
      oldItem
    );
    let moveResult = tryMoveItems(
      item,
      oldItem,
      moveNewItemX,
      [...otherItems],
      potentialBlockers,
      model
    );

    if (!moveResult.valid) {
      /*
    We could not rotate item around it's own axis without exceeding max width,
    but let's see if there is enough space to rotate at all.
    */
      const originalMargin = moveResult.invalidityMargin;

      //First, let's try to the right...
      let newMoveDistance = moveNewItemX + moveResult.invalidityMargin;

      moveResult = tryMoveItems(
        item,
        oldItem,
        newMoveDistance,
        [...otherItems],
        potentialBlockers,
        model
      );

      if (!moveResult.valid) {
        // ...and then to the left.
        newMoveDistance = moveNewItemX - originalMargin;

        moveResult = tryMoveItems(
          item,
          oldItem,
          newMoveDistance,
          [...otherItems],
          potentialBlockers,
          model
        );

        if (!moveResult.valid) {
          // Item cannot be rotated so update is invalid.
          return false;
        }
      }
    }

    updatedItem = moveResult.recenteredItem;
    allItems = moveResult.allItems;
  }

  const sortedItems = options.postponeSort
    ? allItems
    : sorter.inDrawOrder(allItems);
  let newParent = {
    ...parent,
    items: sortedItems,
  };

  // When updating we need to reconnect all our parts to see if they still fit
  if (item.parts && !options.keepParts) {
    const newItem = newParent.items.find(
      parentItem => parentItem.itemid === item.itemid
    );
    // Remove old parts from items
    newItem.items = newItem.items
      ? newItem.items.filter(
          i =>
            ![
              ...Object.values(newItem.parts),
              ...Object.values(oldItem.parts),
            ].some(part => part === i.id)
        )
      : [];

    newParent = connectParts(newItem, newParent, parent, model, options);
  }

  makeSpaceForChild(updatedItem, newParent, model);

  if (parent === model) {
    return newParent;
  } else {
    const out = _.cloneDeep(model);
    replace(out.items, newParent);
    return out;
  }
}
